import React, { useState, useEffect } from 'react';
import { Icons } from './Icon.jsx';
import { getNodeChildren } from '../utils.js';
import { XMarkIcon } from '@heroicons/react/24/outline';

const HierarchyTreeItem = ({
  node,
  allNodes,
  selectedNodeIds,
  onSelect,
  onMove,
  onUpdate,
  onContextMenu,
  depth,
  includedNodeIds,
  searchTerm,
  flatList,
  onFocusItem,
  showChildren = true,
  collapsedNodeIds,
  onToggleNodeExpansion
}) => {
  const isSelected = selectedNodeIds.includes(node.id);

  // Get normal children from the scene
  let children = showChildren
    ? getNodeChildren(allNodes, node.id).filter(child => !includedNodeIds || includedNodeIds.has(child.id))
    : [];

  const hasChildren = children.length > 0;

  const [isDragOver, setIsDragOver] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editLabel, setEditLabel] = useState(node.label);

  const isExpanded = searchTerm ? true : !collapsedNodeIds?.has(node.id);

  useEffect(() => {
    setEditLabel(node.label);
  }, [node.label]);

  // Handle Enter to Rename
  useEffect(() => {
      if (!isSelected || isEditing) return;

      const handleGlobalKeyDown = (e) => {
          if (e.key === 'Enter') {
              if (document.activeElement.tagName !== 'INPUT' && document.activeElement.tagName !== 'TEXTAREA') {
                   e.preventDefault();
                   startRename(e);
              }
          }
      };

      window.addEventListener('keydown', handleGlobalKeyDown);
      return () => window.removeEventListener('keydown', handleGlobalKeyDown);
  }, [isSelected, isEditing]);

  const handleDragStart = (e) => {
    e.stopPropagation();
    e.dataTransfer.setData('nodeId', node.id);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();

    const rect = e.currentTarget.getBoundingClientRect();
    const y = e.clientY - rect.top;
    const height = rect.height;

    // Define zones
    if (y < height * 0.25) {
        setIsDragOver('top');
    } else if (y > height * 0.75) {
        setIsDragOver('bottom');
    } else {
        setIsDragOver('middle');
    }
  };

  const handleDragLeave = () => {
    setIsDragOver(null);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(null);
    const dragId = e.dataTransfer.getData('nodeId');
    if (!dragId) return;

    // Check if dragging a selected node, if so move all selected nodes
    let idsToMove = [dragId];
    if (selectedNodeIds.includes(dragId)) {
        idsToMove = selectedNodeIds;
    }

    if (idsToMove.includes(node.id)) return;

    // Map visual zones to logical actions
    const action = isDragOver === 'top' ? 'above' : isDragOver === 'bottom' ? 'below' : 'inside';
    onMove(idsToMove, node.id, action);
  };

  const startRename = (e) => {
      if (e) e.stopPropagation();
      setIsEditing(true);
  };

  const finishRename = () => {
      setIsEditing(false);
      if (editLabel.trim() && editLabel !== node.label) {
          onUpdate(node.id, { label: editLabel });
      } else {
          setEditLabel(node.label);
      }
  };

  const handleKeyDown = (e) => {
      if (e.key === 'Enter') {
          finishRename();
      } else if (e.key === 'Escape') {
          setIsEditing(false);
          setEditLabel(node.label);
      }
      e.stopPropagation();
  };

  const handleMouseDown = (e) => {
    e.stopPropagation();
    if (e.button !== 0) return;

    if (e.ctrlKey || e.metaKey || e.shiftKey) return;

    if (node.props.locked) return;

    if (!isSelected) {
        onSelect([node.id]);
    }
  };

  const handleClick = (e) => {
      e.stopPropagation();

      if (node.props.locked) return;

      // Handle Modifiers
      if (e.shiftKey && selectedNodeIds.length > 0 && flatList) {
          const lastSelectedId = selectedNodeIds[selectedNodeIds.length - 1];
          const lastIndex = flatList.indexOf(lastSelectedId);
          const currentIndex = flatList.indexOf(node.id);

          if (lastIndex !== -1 && currentIndex !== -1) {
              const start = Math.min(lastIndex, currentIndex);
              const end = Math.max(lastIndex, currentIndex);
              const range = flatList.slice(start, end + 1);
              onSelect(range);
              return;
          }
      }

      if (e.ctrlKey || e.metaKey) {
          if (isSelected) {
             onSelect(selectedNodeIds.filter(id => id !== node.id));
          } else {
             onSelect([...selectedNodeIds, node.id]);
          }
          return;
      }

      onSelect([node.id]);
  };

  const handleContextMenu = (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (!isSelected) {
          onSelect([node.id]);
      }
      onContextMenu(e, node);
  };

  const isLocked = !!node.props.locked;
  const isVisible = node.props.visibility !== false;
  const isEnabled = node.props.enabled !== false;

  const toggleVisibility = () => {
      onUpdate(node.id, { props: { visibility: !isVisible } });
  };

  const toggleEnabled = () => {
      onUpdate(node.id, { props: { enabled: !isEnabled } });
  };

  const toggleLocked = () => {
      const newLocked = !isLocked;
      onUpdate(node.id, { props: { locked: newLocked } });
      if (newLocked && isSelected) {
           onSelect(selectedNodeIds.filter(id => id !== node.id));
      }
  };

  const Chevron = isExpanded ? Icons.ChevronDown : Icons.ChevronRight;
  const isPrefab = !!node.props?.prefabId;

  return (
    <>
      <div
        draggable
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onMouseDown={handleMouseDown}
        onClick={handleClick}
        onContextMenu={handleContextMenu}
        className={`
          flex items-center gap-1 py-1.5 cursor-pointer border-l-2 transition-all relative pr-2 group
          ${isSelected
            ? 'bg-blue-600 border-blue-600 text-white'
            : 'border-transparent text-admin_text_secondary hover:bg-gray-100 hover:text-admin_text'}
          ${!isEnabled ? 'opacity-50' : ''}
        `}
        style={{ paddingLeft: `${depth * 12 + 4}px` }}
      >
        {/* Drop Indicators */}
        {isDragOver === 'top' && <div className="absolute top-0 left-0 right-0 h-0.5 bg-blue-500 z-50 pointer-events-none" />}
        {isDragOver === 'bottom' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-500 z-50 pointer-events-none" />}
        {isDragOver === 'middle' && <div className="absolute inset-0 bg-blue-500/20 z-50 pointer-events-none border border-blue-500" />}

        {/* Expander - Only show if hasChildren */}
        <div
            className={`w-4 h-4 flex items-center justify-center cursor-pointer shrink-0 ${isSelected ? 'text-blue-100 hover:text-white' : 'hover:text-admin_text'}`}
            onClick={(e) => {
                if (hasChildren) {
                    e.stopPropagation();
                    if (onToggleNodeExpansion) {
                        onToggleNodeExpansion(node.id);
                    }
                }
            }}
            onMouseDown={(e) => e.stopPropagation()}
        >
            {hasChildren && <Chevron className="w-3 h-3" />}
        </div>

        {/* Prefab Icon */}
        {isPrefab && (
            <Icons.Prefab className={`w-3.5 h-3.5 shrink-0 ${isSelected ? 'text-white' : 'text-blue-500'}`} />
        )}

        {isEditing ? (
            <input
                autoFocus
                type="text"
                value={editLabel}
                onChange={(e) => setEditLabel(e.target.value)}
                onBlur={finishRename}
                onKeyDown={handleKeyDown}
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
                className="flex-1 bg-admin_canvas border border-blue-500 rounded px-1 text-xs text-admin_text outline-none min-w-0"
            />
        ) : (
            <span
                className="truncate flex-1 font-medium select-none"
                onDoubleClick={(e) => {
                    e.stopPropagation();
                    if (onFocusItem) onFocusItem(node);
                }}
            >
                {node.label}
            </span>
        )}

        {/* Visibility and Selection Controls */}
        <div className={`flex items-center gap-1 pl-1 shrink-0 ${isVisible && !isLocked && isEnabled ? 'opacity-0 group-hover:opacity-100' : 'opacity-100'} transition-opacity`}>
            {/* Checkbox - Enabled/Disabled */}
            <button
                onClick={(e) => { e.stopPropagation(); toggleEnabled(); }}
                className={`p-0.5 rounded transition-colors ${
                    !isEnabled
                       ? 'text-orange-500 opacity-100'
                       : isSelected
                           ? 'hover:bg-blue-500 text-blue-200 hover:text-white'
                           : 'hover:bg-gray-200/50 text-gray-400 hover:text-gray-600'
                }`}
                title={isEnabled ? "Disable (hidden in live mode)" : "Enable"}
            >
                {isEnabled ? <Icons.Check className="w-3.5 h-3.5"/> : <XMarkIcon className="w-3.5 h-3.5"/>}
            </button>
            {/* Eye - Visibility */}
            <button
                onClick={(e) => { e.stopPropagation(); toggleVisibility(); }}
                className={`p-0.5 rounded ${isSelected ? 'hover:bg-blue-500 text-blue-200 hover:text-white' : 'hover:bg-gray-200/50 text-gray-400 hover:text-gray-600'}`}
                title={isVisible ? "Hide (hidden in edit mode)" : "Show"}
            >
                {isVisible ? <Icons.Eye className="w-3.5 h-3.5"/> : <Icons.EyeOff className="w-3.5 h-3.5"/>}
            </button>
            {/* Lock - Selectable */}
            <button
                onClick={(e) => { e.stopPropagation(); toggleLocked(); }}
                 className={`p-0.5 rounded transition-colors ${
                     isLocked
                        ? 'text-red-500 opacity-100'
                        : isSelected
                            ? 'hover:bg-blue-500 text-blue-200 hover:text-white'
                            : 'hover:bg-gray-200/50 text-gray-400 hover:text-gray-600'
                 }`}
                 title={isLocked ? "Unlock Selection" : "Lock Selection"}
            >
                {isLocked ? <Icons.Lock className="w-3.5 h-3.5"/> : <Icons.Unlock className="w-3.5 h-3.5"/>}
            </button>
        </div>
      </div>

      {isExpanded && children.map(child => (
        <HierarchyTreeItem
          key={child.id}
          node={child}
          allNodes={allNodes}
          selectedNodeIds={selectedNodeIds}
          onSelect={onSelect}
          onMove={onMove}
          onUpdate={onUpdate}
          onContextMenu={onContextMenu}
          depth={depth + 1}
          includedNodeIds={includedNodeIds}
          searchTerm={searchTerm}
          flatList={flatList}
          onFocusItem={onFocusItem}
          showChildren={showChildren}
          collapsedNodeIds={collapsedNodeIds}
          onToggleNodeExpansion={onToggleNodeExpansion}
        />
      ))}
    </>
  );
};

export default HierarchyTreeItem;
